import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import UIkit from "uikit";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";

import OpsSubscriptionRequired, {
  hasOpsSubscription,
} from "../../../common/OpsSubscriptionRequired";
import RecordsPerPage from "../../../common/RecordsPerPage";
import Pagination from "../../../common/Pagination";
import StorageStockAdjust from "./add";
import { AppContexts } from "../../../../providers";
import StorageService from "../../../../services/storage.service";
import {
  DEFAULT_RPP,
  INITIAL_STOCK,
  STOCK_STATUS_OPTIONS,
  availableQuantity,
  formatDateTime,
  labelFor,
  optionName,
  stockStatus,
} from "../data";

const statusBadge = (row) => {
  const status = stockStatus(row);
  if (status === "out") return <span className="uk-badge uk-background-danger">Out</span>;
  if (status === "critical") return <span className="uk-badge uk-background-danger">Critical</span>;
  if (status === "low") return <span className="uk-badge uk-background-warning">Low</span>;
  return <span className="uk-badge">OK</span>;
};

const upsertMovementBalance = (balances, movement, locationKey, locationNameKey, locationCodeKey, afterKey) => {
  const locationId = movement[locationKey];
  const afterQuantity = movement[afterKey];
  if (!locationId || afterQuantity == null) return;

  const key = `${locationId}:${movement.item_id}`;
  const existing = balances.get(key);
  const recordedAt = movement.recorded_at || movement.occurred_at;
  if (existing?.recorded_at && recordedAt && new Date(existing.recorded_at) > new Date(recordedAt)) {
    return;
  }

  balances.set(key, {
    id: `movement-${key}`,
    location_id: locationId,
    item_id: movement.item_id,
    location_name: movement[locationNameKey],
    location_code: movement[locationCodeKey],
    item_name: movement.item_name,
    sku: movement.sku,
    unit: movement.unit,
    on_hand: Number(afterQuantity) || 0,
    reserved: 0,
    damaged: 0,
    reorder_point: null,
    par_level: null,
    last_movement_at: movement.occurred_at || movement.recorded_at,
    recorded_at: recordedAt,
  });
};

const stockFromMovements = (movements = []) => {
  const balances = new Map();

  movements.forEach((movement) => {
    upsertMovementBalance(
      balances,
      movement,
      "from_location_id",
      "from_location_name",
      "from_location_code",
      "from_after",
    );
    upsertMovementBalance(
      balances,
      movement,
      "to_location_id",
      "to_location_name",
      "to_location_code",
      "to_after",
    );
  });

  return Array.from(balances.values());
};

export default function StorageStockIndex() {
  const [cookies] = useCookies(["records-per-page"]);
  const { user: account } = useContext(AppContexts.AuthContext);
  const opsSubscriptionLocked = !hasOpsSubscription(account);
  const recordsPerPage =
    Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  const [stock, setStock] = useState(INITIAL_STOCK);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stockSource, setStockSource] = useState("");
  const [criteria, setCriteria] = useState({
    search: "",
    location_id: "",
    status: "",
    page: 0,
  });
  const [editingEntry, setEditingEntry] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const loadStock = useCallback(async () => {
    try {
      const resp = await StorageService.listStock({ page: 0, per: 1000 });
      const stockRows = resp.data?.paginated || (Array.isArray(resp.data) ? resp.data : []);
      if (stockRows.length) {
        setStock(stockRows);
        setStockSource("balances");
        return;
      }

      const movementsResp = await StorageService.listMovements({ page: 0, per: 1000 });
      const movementRows = movementsResp.data?.paginated || [];
      setStock(stockFromMovements(movementRows));
      setStockSource(movementRows.length ? "movements" : "empty");
    } catch (err) {
      setStockSource("error");
      UIkit.notification("Failed to load stock levels.", "error");
    }
  }, []);

  const loadChoices = useCallback(async () => {
    try {
      const [locationsResp, itemsResp] = await Promise.all([
        StorageService.listLocations({ per: 1000 }),
        StorageService.listItems({ per: 1000 }),
      ]);
      setLocations(locationsResp.data?.paginated || []);
      setItems(itemsResp.data?.paginated || []);
    } catch (err) {
      UIkit.notification("Failed to load stock item choices.", "error");
    }
  }, []);

  useEffect(() => {
    document.title = "Stock Levels - RueBaRue";
    loadChoices();
    loadStock();
  }, [loadChoices, loadStock]);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
      page: ["search", "location_id", "status"].includes(key)
        ? 0
        : prev.page,
    }));
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((prev) => ({ ...prev, page: newPageIndex }));
  }, []);

  const enrichedStock = useMemo(() => {
    return stock.map((row) => {
      const location = locations.find((loc) => String(loc.id) === String(row.location_id)) || {};
      const item = items.find((storageItem) => String(storageItem.id) === String(row.item_id)) || {};
      return {
        ...row,
        branch_id: row.branch_id || location.branch_id,
        location_name: row.location_name || location.name,
        location_code: row.location_code || location.code,
        item_name: row.item_name || item.name,
        sku: row.sku || item.sku,
        unit: row.unit || item.unit,
      };
    });
  }, [stock, locations, items]);

  const filtered = useMemo(() => {
    return enrichedStock.filter((row) => {
      if (criteria.location_id && String(row.location_id || "") !== criteria.location_id) return false;
      if (criteria.status && stockStatus(row) !== criteria.status) return false;

      if (criteria.search) {
        const query = criteria.search.toLowerCase();
        const haystack = `${row.item_name || ""} ${row.sku || ""} ${
          row.location_name || ""
        } ${row.location_code || ""
        }`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      return true;
    });
  }, [enrichedStock, criteria]);

  useEffect(() => {
    if (criteria.page > 0 && criteria.page >= Math.ceil(filtered.length / recordsPerPage)) {
      setCriteria((prev) => ({ ...prev, page: 0 }));
    }
  }, [criteria.page, filtered.length, recordsPerPage]);

  const handleSaveStock = useCallback(async (payload) => {
    setIsProcessing(true);
    try {
      await StorageService.createStockAction(payload);
      await loadStock();
      UIkit.notification("Stock action saved", "success");
      return true;
    } catch (err) {
      UIkit.notification("Failed to save stock action.", "error");
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [loadStock]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / recordsPerPage));
  const pageRows = filtered.slice(
    criteria.page * recordsPerPage,
    criteria.page * recordsPerPage + recordsPerPage,
  );

  return (
    <>
      <section>
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Stock Levels</h1>
        </div>

        {opsSubscriptionLocked && <OpsSubscriptionRequired />}

        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <button
              className="uk-button hms-btn hms-red-btn"
              uk-toggle="target: #modal-storage-stock-add"
              type="button"
              onClick={() => setEditingEntry(null)}
              disabled={opsSubscriptionLocked}
              title={opsSubscriptionLocked ? "Ops Subscription Required" : undefined}
            >
              <span uk-icon="icon: plus; ratio: 0.7"></span> Stock Action
            </button>
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <form
                  className="uk-form uk-flex uk-flex-middle hms-form"
                  onSubmit={(event) => event.preventDefault()}
                >
                  <div className="uk-inline uk-margin-small-right">
                    <div className="uk-search uk-search-default uk-form-custom">
                      {criteria.search ? (
                        <button
                          type="button"
                          className="uk-form-icon-flip uk-form-icon"
                          uk-icon="icon: close"
                          onClick={() => handleCriteriaChange("search", "")}
                          aria-label="Clear search"
                        ></button>
                      ) : (
                        <span className="uk-search-icon-flip" uk-search-icon="true" />
                      )}
                      <input
                        className="uk-search-input"
                        type="search"
                        placeholder="Search item, SKU, or location"
                        value={criteria.search}
                        onChange={(event) => handleCriteriaChange("search", event.target.value)}
                      />
                    </div>
                  </div>

                  {[
                    [
                      "location_id",
                      [{ label: "All Locations", value: "" }, ...locations.map((loc) => ({ label: loc.name, value: String(loc.id) }))],
                      "All Locations",
                    ],
                    ["status", STOCK_STATUS_OPTIONS, "All Status"],
                  ].map(([key, options, fallback]) => (
                    <div
                      key={key}
                      className="uk-inline uk-margin-small-right"
                      uk-form-custom="target: > * > span:first-child"
                    >
                      <select
                        value={criteria[key]}
                        onChange={(event) => handleCriteriaChange(key, event.target.value)}
                      >
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                        <span className="select-value">
                          {criteria[key] ? labelFor(options, criteria[key]) : fallback}
                        </span>
                        <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                      </button>
                    </div>
                  ))}
                </form>
              </Flex>

              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">Location</th>
                    <th className="uk-table-expand">Item</th>
                    <th>SKU</th>
                    <th>On Hand</th>
                    <th>Available</th>
                    <th>Reserved</th>
                    <th>Damaged</th>
                    <th>Reorder</th>
                    <th>Par</th>
                    <th>Status</th>
                    <th>Last Movement</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 && (
                    <tr>
                      <td colSpan={12}>
                        <div className="uk-text-center uk-padding-small uk-text-meta">
                          {stock.length > 0
                            ? "No stock matches the current filters."
                            : stockSource === "error"
                              ? "Stock levels could not be loaded from the API."
                              : "No stock levels returned from the API."}
                        </div>
                      </td>
                    </tr>
                  )}

                  {pageRows.map((row) => (
                    <tr key={row.id}>
                      <td className="semi-bold-font">
                        {row.location_name || optionName(locations, row.location_id)}
                        <div className="uk-text-meta">{row.location_code || "No code"}</div>
                      </td>
                      <td>{row.item_name || optionName(items, row.item_id)}</td>
                      <td>{row.sku || "-"}</td>
                      <td>{row.on_hand ?? 0}</td>
                      <td>{availableQuantity(row)}</td>
                      <td>{row.reserved ?? 0}</td>
                      <td>{row.damaged ?? 0}</td>
                      <td>{row.reorder_point ?? "-"}</td>
                      <td>{row.par_level ?? "-"}</td>
                      <td>{statusBadge(row)}</td>
                      <td>{formatDateTime(row.last_movement_at)}</td>
                      <td className="hms-action-btn">
                        <div className="uk-inline">
                          <button
                            className="uk-button"
                            type="button"
                            uk-toggle="target: #modal-storage-stock-add"
                            uk-tooltip="Stock Action"
                            onClick={() => setEditingEntry(row)}
                            disabled={opsSubscriptionLocked}
                            title={opsSubscriptionLocked ? "Ops Subscription Required" : undefined}
                          >
                            <img src="/images/edit.svg" alt="Stock Action" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Flex alignment="between top" className="guest-form-top-filters">
                <Pagination
                  count={totalPages}
                  index={Math.min(criteria.page, totalPages - 1)}
                  onIndexChanged={handleIndexChanged}
                />
                <RecordsPerPage />
              </Flex>
            </div>
          </div>
        </div>
      </section>

      <StorageStockAdjust
        entry={editingEntry}
        locations={locations}
        items={items}
        flagIsProcessing={isProcessing}
        onSave={handleSaveStock}
        onCancel={() => setEditingEntry(null)}
      />
    </>
  );
}
